"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/nextjs";
import { Effect } from "effect";
import { RpcClient } from "@effect/rpc";
import { featureAnnouncementsUserEq, FeatureAnnouncementsUserRpc } from "@/lib/effect-query";
import type { AnnouncementWithSteps } from "@/presentation/rpc/feature-announcements-rpc";
import type { AnnouncementData } from "./types";

// ============================================================================
// Query Key Factory
// ============================================================================

export const ANNOUNCEMENT_QUERY_KEYS = {
  all: ["feature-announcements"] as const,
  active: (featureKey: string) => [...ANNOUNCEMENT_QUERY_KEYS.all, "active", featureKey] as const
};

// ============================================================================
// RPC-to-UI Mapping
// ============================================================================

function rpcToAnnouncementData(rpc: AnnouncementWithSteps): AnnouncementData {
  return {
    featureKey: rpc.announcement.featureKey,
    title: rpc.announcement.title,
    badgeText: rpc.announcement.badgeText,
    steps: [...rpc.steps].sort((a, b) => a.sortOrder - b.sortOrder).map(s => ({
      sortOrder: s.sortOrder,
      description: s.description,
      mediaType: s.mediaType,
      mediaUrl: s.mediaUrl
    }))
  };
}

// ============================================================================
// Query Options
// ============================================================================

const activeAnnouncementQueryOptions = (featureKey: string, enabled: boolean) => ({
  ...featureAnnouncementsUserEq.queryOptions({
    queryKey: ANNOUNCEMENT_QUERY_KEYS.active(featureKey),
    queryFn: () => Effect.gen(function* () {
      const client = yield* RpcClient.make(FeatureAnnouncementsUserRpc);
      return yield* client.getActiveAnnouncement({
        featureKey
      });
    }).pipe(Effect.scoped)
  }),
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  enabled
});

// ============================================================================
// Hook
// ============================================================================

export function useActiveAnnouncement(featureKey) {
  const $ = _c(9);
  const {
    isSignedIn
  } = useAuth();
  const t0 = featureKey ?? "";
  const t1 = !!featureKey && isSignedIn === true;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = activeAnnouncementQueryOptions(t0, t1);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data,
    isPending,
    isFetching,
    error
  } = useQuery(t2);
  const isLoading = isPending && isFetching;
  let t3;
  if ($[3] !== data) {
    t3 = data?.announcement ? rpcToAnnouncementData(data.announcement) : null;
    $[3] = data;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const announcement = t3;
  let t4;
  if ($[5] !== announcement || $[6] !== error || $[7] !== isLoading) {
    t4 = {
      data: announcement,
      isLoading,
      error
    };
    $[5] = announcement;
    $[6] = error;
    $[7] = isLoading;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}